import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, Users as UsersIcon } from "lucide-react";
import { getMeta } from "./calendarTypes";
import { buildWeekDays, formatHM, formatDayLong, isSameDay, FR_DAYS_SHORT } from "./calendarHelpers";
import { translateFr as tfr } from "@/i18n";

/**
 * AgendaView — vertical timeline view used as the mobile experience and
 * as the "Agenda" option on desktop.
 *
 * Layout:
 *  • Day-strip header (Lun..Dim) with the selected day highlighted
 *  • A list of "day sections": the currently selected day is expanded with a
 *    timeline of cards; other days are collapsed with a count.
 */
export default function AgendaView({
  weekStart, events, selectedDate, onSelectDate, onEventClick, onAddEvent,
}) {
  const today = useMemo(() => new Date(), []);
  const days = useMemo(() => buildWeekDays(weekStart), [weekStart]);
  const byDay = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.toDateString()] = []; });
    events.forEach((e) => {
      const k = new Date(e.starts_at).toDateString();
      if (m[k]) m[k].push(e);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
    return m;
  }, [events, days]);

  // Multi-expand: keep selected day expanded + allow toggling others.
  const [expanded, setExpanded] = useState(() => new Set([selectedDate.toDateString()]));
  const toggle = (d) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const k = d.toDateString();
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Make sure the externally-selected day is auto-expanded
  React.useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(selectedDate.toDateString());
      return next;
    });
  }, [selectedDate]);

  return (
    <div className="space-y-4" data-testid="cal-agenda-view">
      {/* Day strip */}
      <div className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 px-1.5 py-3">
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => {
            const isSel = isSameDay(d, selectedDate);
            const isToday = isSameDay(d, today);
            const hasEvents = (byDay[d.toDateString()] || []).length > 0;
            return (
              <button
                key={d.toISOString()}
                onClick={() => onSelectDate?.(d)}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 py-2 rounded-xl transition-all duration-150",
                  isSel ? "bg-violet-100 ring-1 ring-violet-300 dark:bg-violet-500/15 dark:ring-violet-500/40" : "hover:bg-accent/50 dark:hover:bg-secondary/60"
                )}
                data-testid={`agenda-daystrip-${d.toISOString().slice(0, 10)}`}
                aria-pressed={isSel}
              >
                <span className={cn(
                  "text-[10px] uppercase tracking-wider",
                  isSel ? "text-violet-700 dark:text-violet-300" : "text-muted-foreground"
                )}>
                  {FR_DAYS_SHORT[(d.getDay() + 6) % 7]}
                </span>
                <span className={cn(
                  "tabular-nums text-base font-semibold flex items-center justify-center",
                  isSel
                    ? "h-8 w-8 rounded-full bg-athlynx text-white"
                    : isToday
                      ? "text-foreground"
                      : "text-foreground/85"
                )}>
                  {d.getDate()}
                </span>
                <span className={cn(
                  "h-1 w-1 rounded-full",
                  hasEvents ? (isSel ? "bg-violet-500" : "bg-foreground/40") : "bg-transparent"
                )} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Day sections */}
      <div className="space-y-3">
        {days.map((d) => {
          const isSel = isSameDay(d, selectedDate);
          const isToday = isSameDay(d, today);
          const items = byDay[d.toDateString()] || [];
          const isOpen = expanded.has(d.toDateString());
          return (
            <section
              key={d.toISOString()}
              className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden"
              data-testid={`agenda-day-${d.toISOString().slice(0, 10)}`}
            >
              <button
                onClick={() => toggle(d)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent/40 dark:hover:bg-secondary/60 transition-colors"
                aria-expanded={isOpen}
              >
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "tabular-nums text-sm font-semibold flex items-center justify-center h-9 w-9 rounded-full",
                    isSel || isToday ? "bg-violet-500 text-white" : "bg-secondary text-foreground dark:bg-secondary/50"
                  )}>
                    {d.getDate()}
                  </span>
                  <div>
                    <div className="text-sm font-semibold capitalize leading-tight">{formatDayLong(d)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {items.length === 0
                        ? tfr("Aucun événement")
                        : `${items.length} ${items.length > 1 ? tfr("événements") : tfr("événement")}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isToday && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-secondary text-foreground/85 dark:bg-secondary/50">
                      {tfr("Aujourd'hui")}
                    </span>
                  )}
                  <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4">
                  {items.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border dark:border-border py-6 px-4 text-center text-[12px] text-muted-foreground">
                      Aucun événement.{" "}
                      <button
                        onClick={() => onAddEvent?.(d)}
                        className="underline text-violet-700 hover:text-violet-800 dark:text-violet-300 dark:hover:text-violet-200"
                      >
                        {tfr("Créer un événement")}
                      </button>
                    </div>
                  ) : (
                    <ol className="relative pl-12">
                      <span className="absolute left-[40px] top-2 bottom-2 w-px bg-border dark:bg-secondary/50" aria-hidden />
                      {items.map((ev) => {
                        const meta = getMeta(ev.type);
                        const Icon = meta.Icon;
                        return (
                          <li key={ev.id} className="relative mb-2.5 last:mb-0">
                            {/* Hour label */}
                            <span className="absolute -left-12 top-3 text-[11px] tabular-nums text-muted-foreground dark:text-muted-foreground/80 w-10 text-right">
                              {formatHM(ev.starts_at)}
                            </span>
                            {/* Timeline dot */}
                            <span className={cn(
                              "absolute -left-1 top-4 h-2 w-2 rounded-full ring-2 ring-background",
                              meta.dot
                            )} />
                            {/* Card */}
                            <button
                              onClick={() => onEventClick?.(ev)}
                              className={cn(
                                "w-full text-left rounded-xl border-l-[3px] px-3.5 py-2.5 transition-all duration-150",
                                meta.bg, meta.border
                              )}
                              data-testid={`agenda-event-${ev.id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  "shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center",
                                  meta.chip
                                )}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className={cn("text-sm font-semibold truncate", meta.text)}>
                                    {ev.title}
                                  </div>
                                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                    {formatHM(ev.starts_at)} – {formatHM(ev.ends_at)}
                                    {ev.location ? ` • ${ev.location}` : ""}
                                  </div>
                                </div>
                                {ev.participants && (
                                  <div className="shrink-0 flex items-center gap-1 text-[11px] text-muted-foreground">
                                    <UsersIcon className="h-3.5 w-3.5" />
                                    <span className="tabular-nums">
                                      {extractCount(ev.participants)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// Extract the first integer present in participants string, otherwise show its raw length cap.
function extractCount(s) {
  const m = /\d+/.exec(String(s || ""));
  if (m) return m[0];
  // fallback — count commas+1 as a rough estimate, capped
  const parts = String(s).split(",").map((x) => x.trim()).filter(Boolean);
  return parts.length || "";
}
